/*
  Mergilo - cart drawer behaviour (sections/drawer-cart.liquid).

  Quantity changes go through the theme's own CartForm.updateCartQty()
  (assets/component-cart.js). That posts to /cart/change.js, re-renders the
  pieces and the subtotal from ?section_id=helper-cart, updates the header
  count, shows the theme's stock alert when a quantity isn't available and
  fires the theme's cart events, so this file never talks to the Cart API
  for quantities. Listeners are delegated to the drawer because the theme
  replaces the pieces wholesale after every change.

  The spotlight under the pieces is filled here too, from Shopify's product
  recommendations (sections/cart-drawer-upsell.liquid), and its Add to cart
  posts to /cart/add.js, then re-renders the pieces the same way the theme
  does after an add.
*/
(() => {
  const drawer = document.getElementById('site-cart-sidebar');
  if (!drawer || drawer.dataset.mgcReady) return;
  drawer.dataset.mgcReady = 'true';

  const scroller = drawer.querySelector('[data-mgc-scroll]');
  const pieces = () => document.getElementById('AjaxCartForm');
  const cartItems = () => {
    const form = pieces();
    return form ? Array.from(form.querySelectorAll('[data-js-cart-item]')) : [];
  };
  let refocus = null;

  const itemByKey = (key) => cartItems().find((el) => el.dataset.id === key) || null;

  // Changes wait here, one per piece, and go to the cart one at a time: the
  // theme addresses a piece by its line number, which shifts when a piece is
  // removed, so each change looks its piece up again just before it is sent.
  const pending = new Map();
  let inFlight = false;
  let debounce = null;
  let unstick = null;

  const show = (item, quantity) => {
    if (quantity === 0) {
      item.classList.add('mgc-item--leaving');
      return;
    }
    const value = item.querySelector('[data-mgc-qty-value]');
    const input = item.querySelector('[data-mgc-qty]');
    if (value) value.textContent = quantity;
    if (input) input.value = quantity;
  };

  const flush = () => {
    if (inFlight) return;
    while (pending.size) {
      const [key, job] = pending.entries().next().value;
      pending.delete(key);
      const form = pieces();
      const item = itemByKey(key);
      if (!item || !form || typeof form.updateCartQty !== 'function') continue;
      refocus = { key, line: item.dataset.line, step: job.step };
      inFlight = true;
      // If the request fails nothing is re-rendered: let the queue move on.
      unstick = setTimeout(() => {
        inFlight = false;
        flush();
      }, 8000);
      form.updateCartQty(item, job.quantity);
      return;
    }
  };

  // Quantity: minus and plus. The figure changes at once; the cart is
  // updated once the taps settle, so several taps send a single request.
  // Minus at one takes the piece out of the cart.
  drawer.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mgc-step]');
    if (!button) return;
    event.preventDefault();
    const item = button.closest('[data-js-cart-item]');
    if (!item || item.classList.contains('mgc-item--leaving')) return;
    const value = item.querySelector('[data-mgc-qty-value]');
    const current = parseInt(value ? value.textContent : item.dataset.qty, 10) || 0;
    const step = parseInt(button.dataset.mgcStep, 10) || 0;
    const quantity = Math.max(0, current + step);
    show(item, quantity);
    pending.set(item.dataset.id, { quantity, step });
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      debounce = null;
      flush();
    }, quantity === 0 ? 0 : 300);
  });

  // After every re-render: empty state, scroll position, keyboard focus, the
  // next change in the queue, and the spotlight.
  const sync = () => {
    const form = pieces();
    if (!form) return;
    const count = form.querySelector('[data-cart-count]');
    drawer.classList.toggle('mgc--empty', !count || parseInt(count.textContent, 10) === 0);

    const open = drawer.classList.contains('sidebar--opened');
    // Re-rendered while closed (an add to cart): open on the newest piece, at the top.
    if (!open && scroller) scroller.scrollTop = 0;

    // Changes still waiting keep showing on the fresh markup.
    pending.forEach((job, key) => {
      const item = itemByKey(key);
      if (item) show(item, job.quantity);
    });

    if (refocus && open) {
      const items = form.querySelectorAll('[data-js-cart-item]');
      const line = parseInt(refocus.line, 10) || 1;
      const same = itemByKey(refocus.key);
      let target = same ? same.querySelector(`[data-mgc-step="${refocus.step}"]`) : null;
      if (!target && items.length) {
        const next = items[Math.min(line, items.length) - 1];
        target = next.querySelector('[data-mgc-step="1"]');
      }
      if (!target) target = drawer.querySelector('.mgc-empty a') || drawer.querySelector('[data-js-close]');
      if (target && (!document.activeElement || document.activeElement === document.body || !drawer.contains(document.activeElement))) {
        target.focus({ preventScroll: true });
      }
    }
    refocus = null;

    clearTimeout(unstick);
    inFlight = false;
    if (pending.size && !debounce) flush();
    if (open) loadUpsell();
  };

  // ---------- the spotlight: one complementary product at a time ----------
  //
  // Shopify's product recommendations are asked for the products that
  // complement each product in the cart ("complementary" is the list set by
  // hand in the Search & Discovery app). The answers are taken in turn, one
  // from each product's list, leaving out anything already in the cart and
  // any second listing of the same name, until there are four.
  // One is shown large with its options and an Add to cart; the others wait
  // in the rail. Asked for when the drawer opens and again only when the
  // products in the cart change, which is also what happens after an add:
  // the piece appears above with the others and the spotlight moves on.
  //
  // The heading has two wordings: the usual one while everything in the cart
  // is from one main category, and another once the cart holds pieces from
  // two or more (each piece carries its main category in data-category).
  const upsell = drawer.querySelector('[data-mgc-upsell]');
  const q = (name) => (upsell ? upsell.querySelector(`[data-mgc-upsell-${name}]`) : null);
  const spot = {
    title: q('title'),
    index: q('index'),
    current: q('current'),
    total: q('total'),
    rail: q('rail'),
    hero: q('hero'),
    img: q('img'),
    name: q('name'),
    price: q('price'),
    pair: q('pair'),
    opts: q('opts'),
    add: q('add'),
    note: q('note'),
  };
  const ready = Boolean(upsell && spot.rail && spot.hero && spot.img && spot.name && spot.price && spot.opts && spot.add);
  const upsellMax = 4;
  const isColour = (name) => /colou?r|shade|finish/i.test(name);
  const pad = (n) => String(n).padStart(2, '0');
  let picks = [];
  let active = 0;
  let chosen = {};
  let upsellFor = null;
  let adding = false;
  let addedAt = 0;
  let switchTimer = null;
  let wordTimer = null;
  let stuck = null;

  const text = (key) => (spot.add ? spot.add.dataset[key] || '' : '');

  // The variants of the piece that agree with the values chosen so far
  // (leaving out one option when asked, to test that option's values).
  const matching = (pick, selection, except) => pick.variants.filter((variant) => pick.options.every((option, i) => (
    option.name === except || !selection[option.name] || variant.options[i] === selection[option.name]
  )));
  const complete = (pick, selection) => pick.onlyDefault || pick.options.every((option) => Boolean(selection[option.name]));
  const resolved = (pick, selection) => {
    if (pick.onlyDefault) return pick.variants[0] || null;
    if (!complete(pick, selection)) return null;
    return matching(pick, selection).find(() => true) || null;
  };

  // Which values start out chosen: colours (so the image agrees with the
  // chips) and options with a single value; a size is always the shopper's.
  const startChosen = (pick) => {
    const selection = {};
    pick.options.forEach((option, i) => {
      if (option.values.length === 1) selection[option.name] = option.values[0];
      else if (isColour(option.name) && pick.selected && pick.selected[i]) selection[option.name] = pick.selected[i];
    });
    return selection;
  };

  const showNote = (message) => {
    if (!spot.note) return;
    spot.note.textContent = message || '';
    spot.note.hidden = !message;
  };

  const heroImage = (pick, selection) => {
    const withImage = matching(pick, selection).find((variant) => variant.image);
    return (withImage && withImage.image) || pick.image || '';
  };

  const renderAdd = (pick) => {
    const variant = resolved(pick, chosen);
    spot.add.disabled = Boolean(variant && !variant.available);
    spot.add.textContent = variant && !variant.available ? text('textSoldout') : text('textAdd');
    spot.price.textContent = (variant && variant.price) || pick.price || '';
  };

  const renderOptions = (pick) => {
    spot.opts.textContent = '';
    if (pick.onlyDefault) return;
    pick.options.forEach((option) => {
      if (option.values.length < 2) return;
      const row = document.createElement('div');
      row.className = 'mgc-upsell__opt';
      row.dataset.mgcUpsellOpt = option.name;
      const label = document.createElement('span');
      label.className = 'mgc-upsell__opt-label';
      label.textContent = option.name;
      row.appendChild(label);
      const chips = document.createElement('div');
      chips.className = 'mgc-upsell__chips';
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', option.name);
      const live = matching(pick, chosen, option.name);
      const index = pick.options.indexOf(option);
      option.values.forEach((value) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'mgc-upsell__chip';
        chip.dataset.mgcUpsellChip = option.name;
        chip.dataset.value = value;
        chip.textContent = value;
        const selected = chosen[option.name] === value;
        chip.classList.toggle('is-selected', selected);
        chip.setAttribute('aria-pressed', selected ? 'true' : 'false');
        if (!live.some((variant) => variant.options[index] === value && variant.available)) chip.classList.add('is-off');
        chips.appendChild(chip);
      });
      row.appendChild(chips);
      spot.opts.appendChild(row);
    });
  };

  const fill = (pick) => {
    const source = pick.source || '';
    spot.img.src = heroImage(pick, chosen);
    spot.img.alt = '';
    spot.hero.href = pick.url || '#';
    spot.name.textContent = pick.title || '';
    spot.name.href = pick.url || '#';
    if (spot.pair) {
      const template = spot.pair.dataset.text || '';
      spot.pair.textContent = source && template ? template.replace('[product]', source) : '';
    }
    renderOptions(pick);
    renderAdd(pick);
    showNote('');
    if (spot.current) spot.current.textContent = pad(active + 1);
    if (spot.total) spot.total.textContent = pad(picks.length);
    if (spot.index) spot.index.hidden = picks.length < 2;
    Array.from(spot.rail.querySelectorAll('[data-mgc-upsell-thumb]')).forEach((thumb) => {
      const on = Number(thumb.dataset.index) === active;
      thumb.classList.toggle('is-active', on);
      thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  const renderActive = (fade) => {
    const pick = picks[active];
    if (!pick) return;
    clearTimeout(switchTimer);
    const settle = () => {
      fill(pick);
      upsell.classList.remove('is-switching');
    };
    if (fade && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      upsell.classList.add('is-switching');
      switchTimer = setTimeout(settle, 170);
    } else {
      settle();
    }
  };

  const renderRail = () => {
    spot.rail.textContent = '';
    picks.forEach((pick, i) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mgc-upsell__thumb';
      button.dataset.mgcUpsellThumb = '';
      button.dataset.index = String(i);
      button.setAttribute('aria-label', pick.title || '');
      button.setAttribute('aria-pressed', 'false');
      if (pick.thumb || pick.image) {
        const img = document.createElement('img');
        img.src = pick.thumb || pick.image;
        img.alt = '';
        img.loading = 'lazy';
        button.appendChild(img);
      }
      li.appendChild(button);
      spot.rail.appendChild(li);
      // The large image is wanted the moment the thumbnail is tapped.
      if (pick.image) new Image().src = pick.image;
    });
  };

  const choose = (i, fade) => {
    if (!picks[i]) return;
    active = i;
    chosen = startChosen(picks[i]);
    renderActive(fade);
  };

  const settleAdd = () => {
    adding = false;
    clearTimeout(stuck);
    spot.add.classList.remove('is-busy', 'is-done');
  };

  // The pieces re-rendered from the theme's cart section, as the theme does
  // after its own add to cart, so the new piece shows above with the others.
  const refreshCart = () => fetch('?section_id=helper-cart')
    .then((response) => response.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const form = pieces();
      const newForm = doc.getElementById('AjaxCartForm');
      const subtotal = document.getElementById('AjaxCartSubtotal');
      const newSubtotal = doc.getElementById('AjaxCartSubtotal');
      if (subtotal && newSubtotal) subtotal.innerHTML = newSubtotal.innerHTML;
      const count = newForm ? newForm.querySelector('[data-cart-count]') : null;
      const total = newForm ? newForm.querySelector('[data-cart-total]') : null;
      if (count) document.querySelectorAll('[data-header-cart-count]').forEach((el) => { el.textContent = count.textContent; });
      if (total) document.querySelectorAll('[data-header-cart-total]').forEach((el) => { el.textContent = total.textContent; });
      if (form && newForm) {
        form.innerHTML = newForm.innerHTML;
        if (typeof form.ajaxifyCartItems === 'function') form.ajaxifyCartItems();
        form.dispatchEvent(new Event('cart-updated'));
      }
    });

  const addToCart = () => {
    const pick = picks[active];
    if (!pick || adding) return;
    showNote('');
    if (!complete(pick, chosen)) {
      // Point at the first option still to choose.
      const missing = pick.options.find((option) => !chosen[option.name]);
      const row = missing ? spot.opts.querySelector(`[data-mgc-upsell-opt="${CSS.escape(missing.name)}"]`) : null;
      if (row) {
        row.classList.add('is-missing');
        const first = row.querySelector('[data-mgc-upsell-chip]');
        if (first) first.focus({ preventScroll: true });
      }
      spot.add.textContent = text('textChoose').replace('[option]', missing ? missing.name.toLowerCase() : '');
      clearTimeout(wordTimer);
      wordTimer = setTimeout(() => {
        if (row) row.classList.remove('is-missing');
        if (picks[active] === pick) renderAdd(pick);
      }, 1600);
      return;
    }
    const variant = resolved(pick, chosen);
    if (!variant || !variant.available) return;
    adding = true;
    spot.add.classList.add('is-busy');
    clearTimeout(stuck);
    stuck = setTimeout(settleAdd, 10000);
    const fail = (message) => {
      settleAdd();
      renderAdd(pick);
      showNote(message || (window.KROWN && KROWN.settings && KROWN.settings.locales && KROWN.settings.locales.cart_general_error) || 'Something went wrong. Please try again.');
    };
    fetch(`${upsell.dataset.addUrl || '/cart/add'}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: `id=${encodeURIComponent(variant.id)}&quantity=1`,
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok || (data && data.status === 422)) {
          fail(data && (typeof data.description === 'string' ? data.description : data.message));
          return null;
        }
        spot.add.classList.remove('is-busy');
        spot.add.classList.add('is-done');
        spot.add.textContent = text('textAdded');
        addedAt = Date.now();
        return refreshCart().catch(() => {
          // The piece is in the cart even if the drawer could not be redrawn.
          settleAdd();
        });
      })
      .catch(() => fail());
  };

  if (ready) {
    spot.rail.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-mgc-upsell-thumb]');
      if (!thumb) return;
      const i = Number(thumb.dataset.index);
      if (i !== active && !adding) choose(i, true);
    });
    spot.opts.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-mgc-upsell-chip]');
      if (!chip || adding) return;
      const pick = picks[active];
      if (!pick) return;
      chosen[chip.dataset.mgcUpsellChip] = chip.dataset.value;
      const row = chip.closest('[data-mgc-upsell-opt]');
      if (row) row.classList.remove('is-missing');
      const image = heroImage(pick, chosen);
      if (spot.img.getAttribute('src') !== image) spot.img.src = image;
      renderOptions(pick);
      renderAdd(pick);
      showNote('');
    });
    spot.add.addEventListener('click', (event) => {
      event.preventDefault();
      addToCart();
    });
  }

  const loadUpsell = () => {
    if (!ready) return;
    const items = cartItems();
    if (spot.title) {
      const categories = new Set(items.map((el) => el.dataset.category).filter(Boolean));
      const heading = (categories.size > 1 && spot.title.dataset.headingMixed) || spot.title.dataset.heading || '';
      spot.title.textContent = heading;
      spot.title.hidden = !heading;
    }
    const titles = {};
    items.forEach((el) => {
      if (el.dataset.productId && !titles[el.dataset.productId]) titles[el.dataset.productId] = el.dataset.productTitle || '';
    });
    const ids = Object.keys(titles);
    const signature = ids.join(',');
    if (signature === upsellFor) return;
    upsellFor = signature;
    if (!ids.length) {
      upsell.hidden = true;
      picks = [];
      spot.rail.textContent = '';
      settleAdd();
      return;
    }
    const requests = ids.slice(0, 3).map((id) => fetch(`${upsell.dataset.url}?section_id=cart-drawer-upsell&product_id=${encodeURIComponent(id)}&limit=10&intent=complementary`)
      .then((response) => (response.ok ? response.text() : ''))
      .catch(() => '')
      .then((html) => ({ id, html })));
    Promise.all(requests).then((results) => {
      if (signature !== upsellFor) return;
      const lists = results.map(({ id, html }) => Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-mgc-pick]')).map((el) => {
        try {
          const pick = JSON.parse(el.textContent);
          if (!pick || !pick.id || !Array.isArray(pick.variants) || !Array.isArray(pick.options)) return null;
          pick.source = titles[id] || '';
          return pick;
        } catch (error) {
          return null;
        }
      }).filter(Boolean));
      // A piece sold as one listing per colour (two "Toro Trousers", say)
      // takes one slot, under its first listing: the other colours are on
      // its chips. Nor is a piece offered when its name is already in the cart.
      const taken = new Set(ids.map(String));
      const names = new Set(Object.keys(titles).map((id) => titles[id].trim().toLowerCase()).filter(Boolean));
      const picked = [];
      const longest = Math.max(0, ...lists.map((list) => list.length));
      for (let i = 0; i < longest && picked.length < upsellMax; i += 1) {
        lists.forEach((list) => {
          const pick = list[i];
          if (!pick || picked.length >= upsellMax || taken.has(String(pick.id))) return;
          const name = String(pick.title || '').trim().toLowerCase();
          if (name && names.has(name)) return;
          taken.add(String(pick.id));
          if (name) names.add(name);
          picked.push(pick);
        });
      }
      const apply = () => {
        if (signature !== upsellFor) return;
        const fade = Boolean(picks.length) && !upsell.hidden;
        picks = picked;
        settleAdd();
        if (!picks.length) {
          upsell.hidden = true;
          spot.rail.textContent = '';
          return;
        }
        renderRail();
        choose(0, fade);
        upsell.hidden = false;
      };
      // Just after an add the button says so for a moment before moving on.
      const wait = addedAt ? Math.max(0, 900 - (Date.now() - addedAt)) : 0;
      addedAt = 0;
      if (wait) setTimeout(apply, wait);
      else apply();
    });
  };

  const form = pieces();
  if (form) new MutationObserver(sync).observe(form, { childList: true });

  // Desktop: the drawer's top edge sits on the page frame's top rule while
  // that rule is on screen, and on the top of the screen once the page has
  // scrolled past it. (Its bottom edge always sits on the bottom of the
  // screen; see the CSS.) Worked out as the drawer opens (the page can't
  // scroll while it is open) and again if the window is resized. Smaller
  // screens keep the inset from the CSS.
  const frame = document.querySelector('.main-container');
  const desktop = window.matchMedia('(min-width: 1024px)');
  const place = () => {
    if (!desktop.matches) {
      drawer.style.removeProperty('--mgc-top');
      return;
    }
    const frameTop = frame ? frame.getBoundingClientRect().top : 0;
    drawer.style.setProperty('--mgc-top', `${Math.max(0, frameTop)}px`);
  };
  new MutationObserver(() => {
    if (!drawer.classList.contains('sidebar--opened')) return;
    place();
    loadUpsell();
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', () => {
    if (drawer.classList.contains('sidebar--opened')) place();
  });

  // Older iOS Safari ignores overscroll-behavior: stop the rubber-band at the
  // ends of the list and keep touches on the header or summary from moving
  // the page behind.
  let lastY = 0;
  drawer.addEventListener('touchstart', (event) => {
    lastY = event.touches[0].clientY;
  }, { passive: true });
  drawer.addEventListener('touchmove', (event) => {
    if (event.touches.length > 1 || !event.cancelable) return;
    const y = event.touches[0].clientY;
    const dy = y - lastY;
    lastY = y;
    if (!scroller || !scroller.contains(event.target)) {
      event.preventDefault();
      return;
    }
    const max = scroller.scrollHeight - scroller.clientHeight;
    if (max <= 0 || (dy > 0 && scroller.scrollTop <= 0) || (dy < 0 && scroller.scrollTop >= max - 1)) {
      event.preventDefault();
    }
  }, { passive: false });
})();
